import { hasUsOnlyData } from './market.js';

/**
 * Curated Stations Service
 * Uses manually curated truck stop database (100% free, accurate)
 */

export class CuratedStationsService {
    constructor() {
        this.stations = [];
        this.loaded = false;
        this.loadStations();
    }

    async loadStations() {
        // The curated list is US truck stops only.
        if (!hasUsOnlyData()) { this.stations = []; this.loaded = true; return; }
        try {
            // Import JSON from public folder (gets deployed with build)
            const response = await fetch(`/data/truck-stops.json`);
            const data = await response.json();
            this.stations = data.stations;
            this.loaded = true;
            console.log(`📚 Loaded ${this.stations.length} curated truck stops from database v${data.version}`);
        } catch (error) {
            console.error('❌ Failed to load curated stations:', error);
            this.stations = [];
            this.loaded = true;
        }
    }

    /**
     * Wait for stations to load
     */
    async ensureLoaded() {
        if (this.loaded) return;
        
        // Wait up to 5 seconds for load
        for (let i = 0; i < 50; i++) {
            if (this.loaded) return;
            await new Promise(resolve => setTimeout(resolve, 100));
        }
    }

    /**
     * Find stations along a route
     * @param {Array} coordinates - Route coordinates
     * @param {Object} options - Filter options
     * @returns {Promise<Array>} Matching stations with distance info
     */
    async findStationsAlongRoute(coordinates, options = {}) {
        await this.ensureLoaded();
        
        const {
            brand = null,
            maxDistance = 5.0 // miles from route (larger for curated data - these are major truck stops)
        } = options;

        console.log(`🔍 Searching curated database for brand: ${brand || 'all'}`);
        console.log(`📊 Database has ${this.stations.length} stations`);
        console.log(`🎯 DEBUG: Using search radius of ${maxDistance} miles (passed from options: ${options.maxDistance})`);

        // Filter by brand if specified
        let filteredStations = this.stations;
        if (brand) {
            const brandKeywords = brand.toLowerCase().split('|').map(b => b.trim());
            filteredStations = this.stations.filter(station => {
                return brandKeywords.includes(station.brand);
            });
            console.log(`Found ${filteredStations.length} stations matching brand`);
        }

        // Filter by distance to route and calculate distance along route
        const nearbyStations = filteredStations
            .map(station => {
                const result = this.getStationRouteInfo(
                    station.lat,
                    station.lng,
                    coordinates
                );
                
                return {
                    ...station,
                    distanceToRoute: result.distanceToRoute,
                    distanceAlongRoute: result.distanceAlongRoute,
                    distanceToDestination: result.distanceToDestination,
                    isRvFriendly: true, // All curated stations are RV-friendly
                    verifyId: `curated:${station.id}`,
                    source: 'curated'
                };
            })
            .filter(station => station.distanceToRoute <= maxDistance)
            .sort((a, b) => a.distanceAlongRoute - b.distanceAlongRoute); // Sort by distance along route

        console.log(`✅ Found ${nearbyStations.length} stations within ${maxDistance} miles of route`);
        return nearbyStations;
    }

    /**
     * Get station info relative to route
     * @returns {Object} { distanceToRoute, distanceAlongRoute, distanceToDestination, nearestPointIndex }
     */
    getStationRouteInfo(stationLat, stationLng, routeCoordinates) {
        let minDistance = Infinity;
        let nearestPointIndex = 0;
        let distanceAlongRoute = 0;

        // Find nearest point on route
        for (let i = 0; i < routeCoordinates.length - 1; i++) {
            const distance = this.distanceToSegment(
                stationLat, stationLng,
                routeCoordinates[i].lat, routeCoordinates[i].lng,
                routeCoordinates[i + 1].lat, routeCoordinates[i + 1].lng
            );
            
            if (distance < minDistance) {
                minDistance = distance;
                nearestPointIndex = i;
            }
        }

        // Calculate distance along route to nearest point
        for (let i = 0; i < nearestPointIndex; i++) {
            distanceAlongRoute += this.haversineDistance(
                routeCoordinates[i].lat,
                routeCoordinates[i].lng,
                routeCoordinates[i + 1].lat,
                routeCoordinates[i + 1].lng
            );
        }
        
        // Calculate distance from station to destination
        let distanceToDestination = 0;
        for (let i = nearestPointIndex; i < routeCoordinates.length - 1; i++) {
            distanceToDestination += this.haversineDistance(
                routeCoordinates[i].lat,
                routeCoordinates[i].lng,
                routeCoordinates[i + 1].lat,
                routeCoordinates[i + 1].lng
            );
        }

        return {
            distanceToRoute: minDistance,
            distanceAlongRoute: Math.round(distanceAlongRoute * 10) / 10, // Round to 1 decimal
            distanceToDestination: Math.round(distanceToDestination * 10) / 10, // Round to 1 decimal
            nearestPointIndex
        };
    }

    /**
     * Get all stations for a specific brand
     */
    getStationsByBrand(brand) {
        return this.stations.filter(s => s.brand === brand.toLowerCase());
    }

    /**
     * Get stations by highway
     */
    getStationsByHighway(highway) {
        return this.stations.filter(s => s.highway === highway.toUpperCase());
    }

    /**
     * Calculate minimum distance from point to route (in miles)
     */
    getMinDistanceToRoute(lat, lng, routeCoordinates) {
        let minDistance = Infinity;

        for (let i = 0; i < routeCoordinates.length - 1; i++) {
            const distance = this.distanceToSegment(
                lat, lng,
                routeCoordinates[i].lat, routeCoordinates[i].lng,
                routeCoordinates[i + 1].lat, routeCoordinates[i + 1].lng
            );
            minDistance = Math.min(minDistance, distance);
        }

        return minDistance;
    }

    /**
     * Calculate distance from point to line segment (in miles)
     */
    distanceToSegment(px, py, x1, y1, x2, y2) {
        const A = px - x1;
        const B = py - y1;
        const C = x2 - x1;
        const D = y2 - y1;

        const dot = A * C + B * D;
        const lenSq = C * C + D * D;
        let param = -1;

        if (lenSq !== 0) {
            param = dot / lenSq;
        }

        let xx, yy;

        if (param < 0) {
            xx = x1;
            yy = y1;
        } else if (param > 1) {
            xx = x2;
            yy = y2;
        } else {
            xx = x1 + param * C;
            yy = y1 + param * D;
        }

        return this.haversineDistance(px, py, xx, yy);
    }

    /**
     * Calculate distance between two points using Haversine formula (in miles)
     */
    haversineDistance(lat1, lon1, lat2, lon2) {
        const R = 3959; // Earth's radius in miles
        const dLat = this.toRad(lat2 - lat1);
        const dLon = this.toRad(lon2 - lon1);
        
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                  Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
                  Math.sin(dLon / 2) * Math.sin(dLon / 2);
        
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    toRad(degrees) {
        return degrees * Math.PI / 180;
    }

    /**
     * Get database statistics
     */
    async getStats() {
        await this.ensureLoaded();
        
        const byBrand = {};
        this.stations.forEach(station => {
            byBrand[station.brand] = (byBrand[station.brand] || 0) + 1;
        });

        return {
            total: this.stations.length,
            byBrand,
            version: '1.0.0',
            lastUpdated: '2024-01-15'
        };
    }
}
